import React, { useState, useEffect } from 'react';
import { Box, TextField, InputAdornment, IconButton, Tooltip, Typography, Chip, Button } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarColumnsButton, GridToolbarFilterButton } from '@mui/x-data-grid';
import { Search, Edit, CheckCircle, Power, PowerOff, Calendar } from 'lucide-react';
import { tokens } from '@/theme/tokens';
import { useSchool } from '@/hooks/useSchool';
import academicSessionService from '@/services/academicSessionService';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { AcademicSessionForm } from './AcademicSessionForm';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { PATHS } from '@/router/paths';

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ p: 1, borderBottom: `1px solid ${tokens.colors.border}`, gap: 1 }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}

export function AcademicSessionListPage() {
  const { schoolId } = useSchool();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchSessions = async () => {
    if (!schoolId) return;
    try {
      setLoading(true);
      const res = await academicSessionService.getSessionsBySchool(schoolId);
      // Ensure array and sort by start_date descending
      const data = Array.isArray(res) ? res : (res?.data || []);
      const sorted = data.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      setSessions(sorted);
    } catch (error) {
      toast.error('Failed to load academic sessions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const handleEdit = (session) => {
    setEditingSession(session);
    setIsFormOpen(true);
  };

  const handleSetCurrent = async (id) => {
    try {
      await academicSessionService.setSessionCurrent(id);
      toast.success('Session set as current successfully');
      fetchSessions();
    } catch (error) {
      toast.error('Failed to set current session');
      console.error(error);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await academicSessionService.updateSessionStatus(id, newStatus);
      toast.success(`Session status updated to ${newStatus}`);
      fetchSessions();
    } catch (error) {
      toast.error('Failed to update session status');
      console.error(error);
    }
  };

  const columns = [
    { 
      field: 'session_name', 
      headerName: 'Session Name', 
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
          <Calendar size={18} color={tokens.colors.primary.main} />
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
          {params.row.is_current && (
            <Chip
              label="Current"
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      ),
    },
    { 
      field: 'start_date', 
      headerName: 'Start Date', 
      flex: 0.9, 
      minWidth: 130,
      renderCell: (params) => dayjs(params.value).format('MMM D, YYYY')
    },
    { 
      field: 'end_date', 
      headerName: 'End Date', 
      flex: 0.9, 
      minWidth: 130,
      renderCell: (params) => dayjs(params.value).format('MMM D, YYYY')
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 0.7,
      minWidth: 110,
      renderCell: (params) => (
        <StatusBadge 
          status={params.value === 'ACTIVE' ? 'success' : 'error'} 
          label={params.value.toUpperCase()} 
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 2.2,
      minWidth: 440,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%', flexWrap: 'nowrap' }}>
          <Button
            size="small"
            variant="outlined"
            color="info"
            startIcon={<Edit size={14} />}
            onClick={() => handleEdit(params.row)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              fontSize: '0.75rem',
              py: 0.5,
              px: 1.2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Edit
          </Button>

          {!params.row.is_current && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<CheckCircle size={14} />}
              onClick={() => handleSetCurrent(params.row.id)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1.5,
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.2,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Set Current
            </Button>
          )}

          <Button
            size="small"
            variant="outlined"
            color={params.row.status === 'ACTIVE' ? 'error' : 'success'}
            startIcon={params.row.status === 'ACTIVE' ? <PowerOff size={14} /> : <Power size={14} />}
            onClick={() => handleToggleStatus(params.row.id, params.row.status)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              fontSize: '0.75rem',
              py: 0.5,
              px: 1.2,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {params.row.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </Button>
        </Box>
      ),
    },
  ];

  const filteredSessions = sessions.filter((s) => 
    s.session_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageHeader 
        title="Academic Sessions" 
        subtitle="Manage academic years and terms for the school."
        backPath={PATHS.SETTINGS}
        actionLabel="Add Session"
        onAction={() => {
          setEditingSession(null);
          setIsFormOpen(true);
        }}
      />

      <WidgetContainer noPadding>
        <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.colors.border}`, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search sessions..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 350 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={tokens.colors.text.secondary} />
                  </InputAdornment>
                ),
              }
            }}
          />
        </Box>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            autoHeight
            rows={filteredSessions}
            columns={columns}
            loading={loading}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            slots={{ toolbar: CustomToolbar }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-row:hover': { backgroundColor: `${tokens.colors.primary.main}08` },
            }}
          />
        </Box>
      </WidgetContainer>

      {/* Form Modal */}
      <AcademicSessionForm
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSuccess={() => {
          setIsFormOpen(false);
          fetchSessions();
        }}
        session={editingSession}
      />
    </Box>
  );
}
